import {
  renderStormReportHtml,
  type StormReportSnapshot,
} from "@roof-hawk/core";
import { useEffect, useState } from "react";

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/storm-report`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** `/s/<token>`, and nothing that merely looks like it. */
export function stormTokenFromPath(pathname: string): string | null {
  const match = /^\/s\/([0-9a-f]{32})\/?$/.exec(pathname);
  return match ? match[1]! : null;
}

type Loaded =
  | { state: "loading" }
  | { state: "open"; html: string }
  | { state: "closed"; message: string };

/**
 * The adjuster's copy.
 *
 * They hold a link and not a login, and row level security shows an
 * unauthenticated visitor nothing at all (ADR 0020) — so the token is the
 * credential and the `storm-report` Edge Function is the only thing that can
 * turn it into a document.
 *
 * The page renders the *stored* snapshot rather than pulling fresh data. Local
 * Storm Reports are preliminary and get revised in quality control, so a page
 * that re-queried would quietly stop matching the copy that was emailed — and
 * the whole reason this document persuades anybody is that it does not move.
 *
 * The report is written into an iframe rather than into this page. It is a
 * complete standalone document with its own stylesheet, built to be printed and
 * to be opened as a file out of an email; dropping it into the app's DOM would
 * put the app's stylesheet and the report's in the same room, and the version
 * the adjuster prints would stop matching the one attached to their email.
 */
export function StormPage({ token }: { token: string }) {
  const [loaded, setLoaded] = useState<Loaded>({ state: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // A POST, reading included. The token is the only credential here, and
        // a query string is written to the platform's request log as a matter
        // of course, kept in browser history, and sent along in a Referer.
        const response = await fetch(FUNCTION_URL, {
          method: "POST",
          headers: {
            apikey: ANON_KEY,
            Authorization: `Bearer ${ANON_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token, action: "open" }),
        });
        const body = await response.json().catch(() => null);
        if (cancelled) return;

        if (response.ok && body?.snapshot) {
          setLoaded({
            state: "open",
            html: renderStormReportHtml(body.snapshot as StormReportSnapshot),
          });
          document.title = "Storm History Report";
        } else {
          setLoaded({
            state: "closed",
            message:
              typeof body?.error === "string"
                ? body.error
                : "That link is no longer available.",
          });
        }
      } catch {
        if (!cancelled) {
          setLoaded({
            state: "closed",
            message:
              "This report could not be loaded. Check the connection and try again.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loaded.state === "loading") {
    return <PlainPage>Loading the report…</PlainPage>;
  }

  if (loaded.state === "closed") {
    return <PlainPage>{loaded.message}</PlainPage>;
  }

  return (
    <iframe
      title="Storm History Report"
      srcDoc={loaded.html}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        border: 0,
      }}
    />
  );
}

/** Deliberately unstyled by the app: this page belongs to a stranger. */
function PlainPage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: "#EDF1F5",
        color: "#16222E",
        font: "16px/1.6 system-ui, sans-serif",
        textAlign: "center",
      }}
    >
      <p>{children}</p>
    </div>
  );
}
